import React from 'react';
import { IconFileText, IconFolderOpen } from '@tabler/icons-react';
import { StatsCardSkeleton } from '../common/SkeletonLoader';

const Dashboard = ({
  myProjects,
  projectsLoading,
  isProcessing,
  setActiveService,
  handleViewProject,
  user = null
}) => {
  const canCreateTasks = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  const synopsisProjects = myProjects.filter(p => p.projectType === 'synopsis' || p.projectType === 'synopsis_generation' || p.projectType === 'synopsis_comparison');
  const transcriptionProjects = myProjects.filter(p => p.projectType === 'transcription_generation');

  return (
    <div key="dashboard-service-interface" className="space-y-4">
      {/* Page title and description */}
      <div className="flex items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-neutral-100">Dashboard</h3>
          <p className="text-gray-600 dark:text-neutral-400 mt-1">Overview and Analytics</p>
        </div>
      </div>

      {/* Project numbers: synopsis_generation & transcription_generation */}
      {projectsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatsCardSkeleton />
          <StatsCardSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-neutral-400">Synopsis</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-neutral-100">
                  {synopsisProjects.length}
                </p>
              </div>
              <div className="bg-gray-600 p-3 rounded-lg">
                <IconFileText className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-neutral-400">Transcription</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-neutral-100">
                  {transcriptionProjects.length}
                </p>
              </div>
              <div className="bg-gray-600 p-3 rounded-lg">
                <IconFileText className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions: create synopsis_generation & transcription_generation */}
      <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-8 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {canCreateTasks ? (
            <>
              <button
                onClick={() => setActiveService('synopsis')}
                className="flex items-center p-4 border border-gray-200 dark:border-neutral-700 rounded-lg hover:border-sky-300 dark:hover:border-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors"
              >
                <div className="bg-gray-600 p-3 rounded-lg mr-4">
                  <IconFileText className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <h4 className="font-medium text-gray-900 dark:text-neutral-100">Generate Synopsis</h4>
                  <p className="text-sm text-gray-600 dark:text-neutral-400">From YouTube URL</p>
                </div>
              </button>

              <button
                onClick={() => setActiveService('transcription')}
                className="flex items-center p-4 border border-gray-200 dark:border-neutral-700 rounded-lg hover:border-sky-300 dark:hover:border-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors"
              >
                <div className="bg-gray-600 p-3 rounded-lg mr-4">
                  <IconFileText className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <h4 className="font-medium text-gray-900 dark:text-neutral-100">Transcription</h4>
                  <p className="text-sm text-gray-600 dark:text-neutral-400">From YouTube URL</p>
                </div>
              </button>
            </>
          ) : (
            <div className="md:col-span-2 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-amber-800 dark:text-amber-200 text-sm font-medium">Only admin and PM can create new tasks. You can view existing projects below.</p>
            </div>
          )}

          <button
            onClick={() => setActiveService('transcriber-projects')}
            className="flex items-center p-4 border border-gray-200 dark:border-neutral-700 rounded-lg hover:border-gray-300 dark:hover:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-900 transition-colors"
          >
            <div className="bg-gray-600 p-3 rounded-lg mr-4">
              <IconFolderOpen className="h-6 w-6 text-white" />
            </div>
            <div className="text-left">
              <h4 className="font-medium text-gray-900 dark:text-neutral-100">View Projects</h4>
              <p className="text-sm text-gray-600 dark:text-neutral-400">Manage all projects</p>
            </div>
          </button>
        </div>
      </div>

      {/* Recent Projects from Creators */}
      {myProjects.length > 0 && (
        <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-8 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-6">Recent Projects</h3>
          <div className="space-y-4">
            {myProjects.slice(0, 5).map((project) => (
              <div key={project.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-neutral-700 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-900 transition-colors">
                <div className="flex items-center">
                  <div className="bg-gray-100 dark:bg-neutral-800 p-2 rounded-lg mr-3">
                    <IconFolderOpen className="h-5 w-5 text-gray-600 dark:text-neutral-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-neutral-100">{project.title ?? project.name}</h4>
                    <p className="text-sm text-gray-600 dark:text-neutral-400">
                      {project.fileCount ?? 0} file{(project.fileCount ?? 0) !== 1 ? 's' : ''} • {project.status ?? '—'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleViewProject(project)}
                  className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 text-sm font-medium"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
